import { Box, Stack, Typography, Button, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import OrderDetails from '@/sections/event/OrderDetails';
import { fCurrency } from '@/utils/formatNumber';
import { useSummary, useTicketsSelected, usePromotions } from '@/store';
import useEventContext from '@/contexts/event/useEventContext';
import { useCheckoutGenerateOrder } from '@/hooks/checkout';
import useMobile from '@/hooks/useMobile';
import Iconify from '@/components/Iconify';
import useRouterTransition from '@/hooks/useRouterTransition';
import useAnalyticsEvents from '@/hooks/useAnalyticsEvents';
import userTracking from '@/hooks/userTracking/index';
import useRecaptcha from '@/contexts/recaptcha/useRecaptcha';
import { shouldEventHideCustomLinks } from '@/config/linksConfig';

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  width: '18px',
  height: '18px',
  padding: '0',
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const BoxStyle = styled(Box)(({ theme }) => ({
  transition: theme.transitions.create(['height'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
}));

export const FOOTER_DESKTOP_HEIGHT = 77;
export const FOOTER_MOBILE_HEIGHT = 77;

function FooterSummary() {
  const { t } = useTranslation();
  const { showCartDetails, oldBuyTickets } = useAnalyticsEvents();
  const { buyTicketsClicked } = userTracking();

  const { handleRecaptcha } = useRecaptcha();

  const {
    summary: {
      total_to_pay,
      discount,
      commissions,
      total_tickets,
      total_processor_fee,
      total_transaction_fee,
      total_service_fee,
    },
  } = useSummary();

  const {
    event: {
      event: { use_seats: useSeats, id: eventId },
      preferences: { currency },
    },
  } = useEventContext();
  const { count, ticketsSelected } = useTicketsSelected();
  const { generateOrder, isLoading } = useCheckoutGenerateOrder();

  const changeByGetWord = shouldEventHideCustomLinks(eventId);

  const onBuyTicketsHandler = async () => {
    const { token, recaptchaToken } = await handleRecaptcha();
    buyTicketsClicked();
    oldBuyTickets();
    generateOrder(token, recaptchaToken);
  };
  const { isMobile } = useMobile();
  const { promotions: promotion, clearPromotion } = usePromotions();
  const [expanded, setExpanded] = useState(false);
  const { isInTransition } = useRouterTransition();

  const expandHandler = () => {
    setExpanded((prev) => !prev);
  };

  const onRemovePromotion = () => {
    clearPromotion();
  };

  const getZone = (info, parentType) => {
    if (parentType !== 'row') return null;
    return `${info.section} - Fila ${info.parent} - Asiento ${info.own}`;
  };

  const getTicketsInfo = () => {
    const ticketsInfo = [];

    ticketsSelected.forEach((t) => {
      if (useSeats) {
        if (ticketsInfo.some((ticket) => ticket.id === t.ticket_type_id)) {
          const founded = ticketsInfo.find(
            (ticket) => ticket.id === t.ticket_type_id,
          );
          founded.qty += 1;
          if (t.objectType === 'Seat')
            founded.zones.push(getZone(t.seatsIoInfo, t?.parent?.type));
        } else {
          ticketsInfo.push({
            id: t.ticket_type_id,
            price: t.price,
            name: t?.category?.name || 'General',
            qty: t.quantity,
            zones:
              t.objectType === 'Seat'
                ? [getZone(t.seatsIoInfo, t?.parent?.type)]
                : [],
          });
        }
      } else {
        if (ticketsInfo.some((ticket) => ticket.id === t.ticket_type_id)) {
          const founded = ticketsInfo.find(
            (ticket) => ticket.id === t.ticket_type_id,
          );
          founded.qty += 1;
        } else {
          ticketsInfo.push({
            id: t.ticket_type_id,
            price: t.price,
            name: t.name,
            qty: t.quantity,
            zones: [],
          });
        }
      }
    });

    return ticketsInfo;
  };

  useEffect(() => {
    if (expanded) {
      showCartDetails();
    }
  }, [expanded]);

  return (
    <BoxStyle
      sx={{
        height: expanded ? 'auto' : FOOTER_MOBILE_HEIGHT,
        background: (theme) => theme.palette.background.default,
        position: 'relative',
        paddingBottom: expanded ? `${FOOTER_MOBILE_HEIGHT}PX` : 0,
      }}
    >
      {expanded && (
        <OrderDetails
          title="Resumen de orden"
          hideTotal
          onRemovePromotion={onRemovePromotion}
          resume={{
            ticketsDetail: getTicketsInfo(),
            subtotalTickets: total_tickets,
            totalProcessorFee: total_processor_fee,
            totalServiceFee: total_service_fee,
            totalTransactionFee: total_transaction_fee,
            total: total_to_pay,
            currency,
            discount,
            promotion: promotion?.code,
            commissions,
            ticketInsurance: '',
          }}
          isSmall
        />
      )}

      <Stack
        direction="row"
        sx={{
          position: 'absolute',
          bottom: 0,
          background: (theme) => theme.palette.background.default,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          height: isMobile
            ? `${FOOTER_MOBILE_HEIGHT}px`
            : `${FOOTER_DESKTOP_HEIGHT}px`,
        }}
      >
        <Box sx={{ minWidth: 160 }}>
          <Stack direction="row" sx={{ width: '100%', alignItems: 'flex-end' }}>
            <Typography
              variant="title"
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                lineHeight: '16px',
              }}
              component="div"
            >
              {`${fCurrency(total_to_pay)} `}
              {currency}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            sx={{ width: '100%', alignItems: 'center', mt: 0.5 }}
          >
            <Box
              onClick={expandHandler}
              sx={{
                '&:hover': {
                  cursor: 'pointer',
                },
              }}
            >
              <Typography
                variant="caption3"
                component="div"
                sx={{
                  fontSize: '10px',
                  lineHeight: '10px',
                  textDecoration: 'underline',
                }}
              >
                {t('checkout.global.ReviewPurchase')}
              </Typography>
            </Box>

            <ExpandMore expand={expanded} onClick={expandHandler}>
              <Iconify
                width={14}
                height={14}
                icon={'material-symbols:keyboard-arrow-up'}
              />
            </ExpandMore>
          </Stack>
        </Box>
        <Box sx={{ flexGrow: 1 }}></Box>
        <Box width={'60%'}>
          <Button
            variant="contained"
            sx={{ height: '45px', width: '100%' }}
            onClick={onBuyTicketsHandler}
            disabled={isInTransition || isLoading}
          >
            {changeByGetWord
              ? t('checkout.buttons.getTickets', { count })
              : t('checkout.buttons.buyTickets', { count })}
          </Button>
        </Box>
      </Stack>
    </BoxStyle>
  );
}

export default FooterSummary;
